import React, { useEffect, useRef, useState } from "react";
import { C } from "../../core/constants";
import { hexToRgba } from "../../core/color";
import { switchLocaleUrl } from "../../core/localePaths";
import { useI18n } from "../../features/i18n/LocaleProvider";

export default function LanguageSwitcher({
  locale,
  onChange,
  acc,
  shouldClose = false,
  onOpenChange = null,
  menuAlign = "end",
  onNavigate = null,
}) {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);
  const languageOptions = [
    { value: "en", label: t("en_short") },
    { value: "vi", label: t("vi_short") },
  ];
  const activeOption = languageOptions.find((option) => option.value === locale) || languageOptions[0];

  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (shouldClose) setIsOpen(false);
  }, [shouldClose]);

  useEffect(() => {
    if (typeof onOpenChange === "function") onOpenChange(isOpen);
  }, [isOpen, onOpenChange]);

  const menuPositionStyle =
    menuAlign === "start"
      ? { left: 0, right: "auto" }
      : { right: 0, left: "auto" };

  return (
    <div ref={rootRef} style={{ position: "relative", zIndex: 60, width: "fit-content", maxWidth: "100%" }}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={t("language")}
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          height: 36,
          borderRadius: 12,
          border: `1px solid ${acc.primary}44`,
          background: `linear-gradient(135deg, ${hexToRgba(acc.primary, 0.14)}, rgba(10,14,34,0.86))`,
          boxShadow: `0 0 20px ${hexToRgba(acc.primary, 0.18)}`,
          backdropFilter: "blur(12px)",
          color: C.textPrimary,
          fontFamily: "'DM Sans',sans-serif",
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          padding: "0 34px 0 10px",
          cursor: "pointer",
        }}
      >
        <span
          style={{
            marginRight: 8,
            color: acc.primary,
            fontSize: 12,
            opacity: 0.95,
            pointerEvents: "none",
          }}
        >
          ◌
        </span>
        {activeOption.label}
        <span
          style={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: `translateY(-50%) ${isOpen ? "rotate(180deg)" : "rotate(0deg)"}`,
            color: acc.primary,
            fontSize: 10,
            opacity: 0.9,
            pointerEvents: "none",
            transition: "transform 0.2s ease",
          }}
        >
          ▾
        </span>
      </button>

      {isOpen && (
        <div
          role="menu"
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            ...menuPositionStyle,
            minWidth: 120,
            padding: 6,
            borderRadius: 12,
            border: `1px solid ${acc.primary}44`,
            background: "rgba(12, 18, 40, 0.95)",
            backdropFilter: "blur(12px)",
            boxShadow: `0 12px 30px rgba(0,0,0,0.45), 0 0 18px ${hexToRgba(acc.primary, 0.2)}`,
          }}
        >
          {languageOptions.map((option) => {
            const isActive = option.value === locale;
            return (
              <button
                key={option.value}
                type="button"
                role="menuitemradio"
                aria-checked={isActive}
                onClick={() => {
                  const targetPath = switchLocaleUrl(window.location.pathname, option.value);
                  if (targetPath !== window.location.pathname) {
                    if (typeof onNavigate === "function") {
                      onNavigate(targetPath, option.value);
                    } else {
                      window.location.assign(targetPath);
                    }
                    setIsOpen(false);
                    return;
                  }
                  onChange(option.value);
                  setIsOpen(false);
                }}
                style={{
                  width: "100%",
                  border: "none",
                  borderRadius: 9,
                  background: isActive ? `${acc.primary}22` : "transparent",
                  color: isActive ? C.textPrimary : C.textSecondary,
                  textAlign: "left",
                  fontFamily: "'DM Sans',sans-serif",
                  fontSize: 12,
                  fontWeight: isActive ? 600 : 500,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  padding: "9px 10px",
                  cursor: "pointer",
                  marginBottom: 4,
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
